import { useMemo } from 'react';

import { Platform } from 'react-native';

import { useSelector } from 'react-redux';

import {
    EpgProgram,
    RBChannel,
    RBProgram,
} from 'store/railsBuilder/reducer.types';
import { RecordingMetadata } from 'store/recordings/reducer.types';
import { Entitlement } from 'store/user/reducer.types';
import { selectNPVRCapability } from 'store/user/selectors';
import { isCapabilityValid } from 'store/utils';

import { convertBooleanStringToBoolean } from 'utils/convertBooleanStringToBoolean';
import { convertStringToBoolean } from 'utils/convertStringToBoolean';

type Props = {
    program?: EpgProgram | RBProgram | RecordingMetadata | undefined;
    channel?: RBChannel | undefined;
};

export default function useRecordingPermissions({
    program,
    channel,
}: Props = {}) {
    const userNPVREntitlements = useSelector(selectNPVRCapability);

    return useMemo(
        () => ({
            ...getRecordingPermissions(channel, program, userNPVREntitlements),
            isRecordingPlayable: checkIfRecordingIsPlayable(channel, program),
        }),
        [channel, program, userNPVREntitlements],
    );
}

export function getRecordingPermissions(
    channel: RBChannel | undefined,
    program: EpgProgram | RBProgram | RecordingMetadata | undefined,
    npvrEntitlement: Entitlement | undefined,
) {
    const userHasRecordingPermissions = isCapabilityValid(npvrEntitlement);
    const channelHasRecordingPermission = !!(
        channel &&
        convertBooleanStringToBoolean(channel.editorial?.nPvrSupport) &&
        convertBooleanStringToBoolean(channel.technical?.nPvrSupport)
    );
    const isRecordingOrHasNPvrSupport = (
        program as RBProgram | EpgProgram | undefined
    )?.seriesRef
        ? convertBooleanStringToBoolean(
              (program as RBProgram | EpgProgram).nPvrSupport,
          )
        : true;

    const programHasRecordingPermission =
        !!program?.isnPvr && isRecordingOrHasNPvrSupport;

    return {
        userHasRecordingPermissions,
        channelHasRecordingPermission,
        programHasRecordingPermission,
        hasGlobalRecordingPermission:
            userHasRecordingPermissions &&
            channelHasRecordingPermission &&
            programHasRecordingPermission,
    };
}

function checkIfRecordingIsPlayable(
    channel: RBChannel | undefined,
    program: EpgProgram | RBProgram | RecordingMetadata | undefined,
) {
    const detail = program as RBProgram;
    const detailMetadata = program as RecordingMetadata;

    const detailNpvrControl = Platform.select({
        web: detail?.clientWebNpvrControl,
        default: detail?.clientMobileNpvrControl,
    });
    const detailMetadataControl = Platform.select({
        web: detailMetadata?.clientWebNpvrControl,
        default: detailMetadata?.clientMobileNpvrControl,
    });
    const channelControl = Platform.select({
        web: channel?.editorial.clientWebNpvrControl,
        default: channel?.editorial.clientMobileNpvrControl,
    });

    if (detailNpvrControl) {
        return convertStringToBoolean(detailNpvrControl);
    }

    if (detailMetadataControl) {
        return convertStringToBoolean(detailMetadataControl);
    }

    if (channelControl) {
        return convertStringToBoolean(channelControl);
    }

    return false;
}
